<script setup lang='ts'>
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';

import useCartWidget from '@/composables/useCartWidget';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';

import CartDetails from './v2/CartDetails.vue';

const props = defineProps<{
  apiKey: string;
}>();
const { t } = useI18n();
const { carts, isEmptyCart } = useCartWidget();
const { apiURL } = storeToRefs(useGlobalConfigurationStore());

const iFrameWrapper = useTemplateRef('widget_wrapper');
const iFrameProviderWidgetHeight = ref<number>(780);
const logoAssetsUrl = ref<string>(window.location.hostname === 'localhost' ? 'http://localhost:5173/public/' : 'https://components.youbook.travel');
const cartWidgetUrl = computed(() => apiURL.value.replace('/v3', '/static/widgets/cart'));
const activeCart = ref(carts.value[0]?.provider);
const hasMultipleProviders = computed(() => carts.value.length > 1);

function resizeIframe(event: any) {
  if (event.data?.type === 'widget-resize') {
    iFrameProviderWidgetHeight.value = event.data.widgetHeight;
  }
}

function getProviderLogo(source: string) {
  return `${logoAssetsUrl.value}/${source}.png`;
}

function setupWidgetHeight() {
  const height = iFrameWrapper.value?.getBoundingClientRect()?.height ?? 1;
  iFrameWrapper.value?.firstElementChild?.setAttribute('height', `${Math.max(height ?? 450, 450)}px`);
}

watchEffect(async () => {
  if (isEmptyCart.value || activeCart.value === 'youbook')
    return;
  const iframeSrc = `${cartWidgetUrl.value}?apiKey=${props.apiKey}&provider=${activeCart.value}`;
  iFrameWrapper.value?.firstElementChild?.setAttribute('src', iframeSrc);
  await nextTick();
  iFrameWrapper.value?.firstElementChild?.setAttribute('width', '100%');
  iFrameWrapper.value?.firstElementChild?.setAttribute('height', '100%');
  iFrameWrapper.value?.firstElementChild?.setAttribute('title', 'cart');
  iFrameWrapper.value?.addEventListener('onload', setupWidgetHeight);
});

watch(carts, () => {
  const providers = carts.value.map(cart => cart.provider);
  if (!activeCart.value || !providers.includes(activeCart.value)) {
    activeCart.value = providers[0];
  }
});

onMounted(async () => {
  window.addEventListener('message', resizeIframe);
});
onUnmounted(() => window.removeEventListener('message', resizeIframe));
</script>

<template>
  <p v-if="isEmptyCart" style="width:100%;text-align:center;">
    {{ t('cart.empty') }}
  </p>
  <section v-else>
    <aside v-if="hasMultipleProviders">
      <button
        v-for="cart in carts" :key="cart.id" type="button" :class="cart.provider === activeCart ? 'active' : ''"
        :aria-pressed="activeCart === cart.provider" @click="activeCart = cart.provider"
      >
        <p>{{ t('cart.disclaimer') }}</p>
        <div class="content_wrapper">
          <img :src="getProviderLogo(cart.provider)" :alt="cart.provider" width="120" height="auto">
          <p>{{ t('cart.article', { count: cart.count }) }}</p>
        </div>
      </button>
    </aside>
    <main v-if="activeCart === 'youbook'">
      <CartDetails />
    </main>
    <main v-else ref="widget_wrapper">
      <iframe title="cart" />
    </main>
  </section>
</template>

<style scoped>
section {
  display: flex;
  max-width: 1200px;
}

aside {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
}

button {
  border: 1px solid hsl(0, 0%, 78%);
  border-right: none;
  border-bottom: none;
  padding: var(--yb-spacing-sm);
  font-size: var(--yb-text-xs);
  font-style: italic;
  background-color: hsl(210, 17%, 98%);

  p {
    text-align: left;
  }

  .content_wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-style: normal;
    font-size: var(--yb-text-sm);
    margin-top: 0.5rem;
  }

  &.active {
    position: relative;
    z-index: 5;
    width: calc(100% + 2px);
    background-color: var(--yb-color-white);
    border-right: var(--yb-color-white);
  }

  &:not(.active) {
    cursor: pointer;
  }

  &:last-child {
    border-bottom: 1px solid hsl(0, 0%, 78%);
  }
}

main {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  min-height: 450px;
  overflow-y: hidden;

  iframe {
    overflow-x: hidden;
    padding: 0.5rem;
  }
}
</style>
